import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Eye, TrendingUp, ArrowRight } from 'lucide-react';
import api from '../services/api';
import Onboarding from '../components/Onboarding';

function StatCard({ label, value, sub, icon: Icon, color = 'var(--blue)' }) {
  return (
    <div className="card" style={{ padding: '20px 22px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <span style={{ fontSize: 12, color: 'var(--text2)', fontWeight: 500 }}>{label}</span>
        <div style={{ width: 30, height: 30, borderRadius: 8, background: 'var(--bg3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon size={14} color={color} />
        </div>
      </div>
      <div style={{ fontSize: 26, fontWeight: 600, letterSpacing: '-0.5px' }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

export default function Dashboard() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/api/campaigns').then(r => setCampaigns(r.data.campaigns || [])).finally(() => setLoading(false));
  }, []);

  const totalSent = campaigns.reduce((s, c) => s + (parseInt(c.emails_sent) || 0), 0);
  const totalOpened = campaigns.reduce((s, c) => s + (parseInt(c.emails_opened) || 0), 0);
  const openRate = totalSent > 0 ? ((totalOpened / totalSent) * 100).toFixed(1) : '0';
  const running = campaigns.filter(c => c.status === 'running');

  const statusBadge = (status) => {
    const map = {
      running: { cls: 'badge-blue', label: 'live' },
      paused: { cls: 'badge-amber', label: 'paused' },
      completed: { cls: 'badge-green', label: 'done' },
      draft: { cls: 'badge-gray', label: 'draft' },
      stopped: { cls: 'badge-red', label: 'stopped' },
    };
    const b = map[status] || map.draft;
    return <span className={`badge ${b.cls}`}>{b.label}</span>;
  };

  return (
    <div>
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 20, fontWeight: 600, letterSpacing: '-0.4px' }}>Dashboard</h1>
        <p style={{ color: 'var(--text2)', fontSize: 13, marginTop: 3 }}>Overview of all your campaigns</p>
      </div>

      <Onboarding />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginBottom: 28 }}>
        <StatCard label="Total sent" value={totalSent.toLocaleString()} icon={Mail} />
        <StatCard label="Open rate" value={`${openRate}%`} sub={`${totalOpened} opened`} icon={Eye} color="var(--green)" />
        <StatCard label="Active campaigns" value={running.length} sub="sending now" icon={TrendingUp} color="var(--blue)" />
      </div>

      <div className="card">
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 14, fontWeight: 500 }}>Recent campaigns</span>
          <button className="btn btn-ghost btn-sm" onClick={() => navigate('/campaigns')}>
            View all <ArrowRight size={12} />
          </button>
        </div>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>Loading…</div>
        ) : campaigns.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center' }}>
            <Mail size={32} color="var(--text3)" style={{ marginBottom: 12 }} />
            <p style={{ color: 'var(--text2)', fontSize: 13 }}>No campaigns yet</p>
            <button className="btn btn-primary btn-sm" style={{ marginTop: 14 }} onClick={() => navigate('/campaigns/new')}>
              Create your first campaign
            </button>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                {['Campaign', 'Status', 'Sent', 'Opened', 'Replied', ''].map(h => (
                  <th key={h} style={{ padding: '10px 20px', textAlign: 'left', fontSize: 11, color: 'var(--text3)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {campaigns.slice(0, 8).map(c => (
                <tr key={c.id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '12px 20px' }}>
                    <div style={{ fontWeight: 500, fontSize: 13 }}>{c.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--text3)' }}>{c.list_name}</div>
                  </td>
                  <td style={{ padding: '12px 20px' }}>{statusBadge(c.status)}</td>
                  <td style={{ padding: '12px 20px', fontSize: 13 }}>{(parseInt(c.emails_sent) || 0).toLocaleString()}</td>
                  <td style={{ padding: '12px 20px', fontSize: 13, color: 'var(--green)' }}>
                    {c.emails_sent > 0 ? `${((c.emails_opened / c.emails_sent) * 100).toFixed(0)}%` : '—'}
                  </td>
                  <td style={{ padding: '12px 20px', fontSize: 13 }}>{parseInt(c.replied_count) || 0}</td>
                  <td style={{ padding: '12px 20px' }}>
                    <button className="btn btn-ghost btn-sm" onClick={() => navigate(`/campaigns/${c.id}`)}>View</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
